import React, { forwardRef, ReactElement, RefAttributes, useImperativeHandle, useRef, useState } from 'react'
import {
  FlatList,
  FlatListProps,
  ListRenderItem,
  StyleProp,
  StyleSheet,
  View,
  ViewProps,
  ViewStyle,
} from 'react-native'

import { Color } from '../../style'

import { sortedRows } from '../../helper'

import Head from './head'
import Row, { Props as TableRowProps } from './row'

const styles = StyleSheet.create({
  container: {},
  head: {
    borderTopColor: Color.calmGrey,
    borderTopWidth: 1,
    flex: 0,
  },
  row: {
    borderBottomColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
})

export interface TableRowItem<T = Record<string, string | number>>
  extends Omit<TableRowProps, 'cols' | 'sizes' | 'condensed' | 'hasAccessory'> {
  data: T
}

export interface TableColSize {
  flex?: number
  width?: number
}

export interface TableCol<T = Record<string, string | number>> {
  key: string
  title?: string | ReactElement | null
  render?: (data: T, key: string, index: number) => ReactElement | undefined
  sort?: ((a: T, b: T) => number) | false
  size?: TableColSize
  help?: string | ReactElement
}

export type TableSortDirection = 'asc' | 'desc'

export interface TablePagination {
  size: number
  page: number
}

export interface TableSort {
  field: string
  direction?: TableSortDirection
}

export interface TableRef {
  scrollToRow: (params: {
    row: TableRowItem<any>
    animated?: boolean | null | undefined
    viewOffset?: number | undefined
    viewPosition?: number | undefined
  }) => void
}

export interface Props<T = any>
  extends Omit<FlatListProps<TableRowItem>, 'data' | 'renderItem' | 'ListHeaderComponent' | 'contentContainerStyle'> {
  rows: TableRowItem<T>[]
  cols?: TableCol<T>[]
  hideHeader?: boolean
  defaultSort?: TableSort
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
  dataIsSorted?: boolean
  locale?: string
  condensed?: boolean
  rowStyle?: StyleProp<ViewStyle>
  cellStyle?: StyleProp<ViewStyle>
  contentContainerStyle?: StyleProp<ViewStyle>
  onScrollToRowFailed?: (info: {
    row: TableRowItem<any>
    highestMeasuredFrameIndex: number
    averageItemLength: number
  }) => void
}

const Table = forwardRef<TableRef, Props>((props, ref) => {
  const {
    rows,
    cols,
    hideHeader,
    sort: propsSort,
    onSortChange,
    dataIsSorted,
    locale,
    condensed,
    defaultSort,
    rowStyle,
    cellStyle,
    contentContainerStyle,
    style,
    onScrollToRowFailed,
    ...otherProps
  } = props
  const colKeys = cols?.map((col) => col.key) || Object.keys(rows?.[0]?.data || {})
  const flatlistRef = useRef<FlatList>(null)
  const hasAccessory = !!rows.find((item) => item.onPress)

  useImperativeHandle(ref, () => ({
    scrollToRow: (params) => {
      const { row, ...otherParams } = params
      const index = items.findIndex((item) => row === item)
      if (index >= 0) flatlistRef.current?.scrollToIndex({ index, ...otherParams })
    },
  }))

  const [sort, setSort] = useState<TableSort>(
    propsSort ||
      defaultSort || {
        field: colKeys?.[0] || '',
      },
  )

  const updateSort = (sort: TableSort) => {
    onSortChange?.(sort)
    setSort(sort)
  }

  const items = !sort.field || dataIsSorted ? rows : sortedRows(rows, cols, sort, locale)

  const renderHeader = () => {
    if (hideHeader) return null

    return (
      <Head
        cols={cols || colKeys.map((key) => ({ key }))}
        style={[styles.row, styles.head]}
        defaultSort={sort}
        onSort={updateSort}
        hasAccessory={hasAccessory}
      />
    )
  }

  const renderRow: ListRenderItem<TableRowItem> = (info) => {
    const { item, index } = info
    const { data, style, cellStyle: itemCellStyle, ...otherItem } = item
    const rowCols =
      cols?.map((col) => col.render?.(data, col.key, index) || (data[col.key] !== undefined ? data[col.key] : '')) ||
      Object.values(data || {})
    const sizes = cols?.map((col) => col.size)
    return (
      <Row
        style={[styles.row, rowStyle, style]}
        cellStyle={[cellStyle, itemCellStyle]}
        testID="Table.Row"
        {...otherItem}
        condensed={condensed}
        hasAccessory={hasAccessory}
        cols={rowCols}
        sizes={sizes}
      />
    )
  }
  const keyExtractor = (row: TableRowItem, index: number) => `${index}`
  return (
    <View style={[styles.container, style]}>
      {rows.length > 0 && renderHeader()}
      <FlatList
        contentContainerStyle={contentContainerStyle}
        ref={flatlistRef}
        data={items}
        renderItem={renderRow}
        keyExtractor={keyExtractor}
        onScrollToIndexFailed={(info) => {
          const { index, ...otherInfo } = info
          const row = items[index]
          onScrollToRowFailed?.({ row, ...otherInfo })
        }}
        testID="Table.Container"
        {...otherProps}
      />
    </View>
  )
}) as ((props: Props & RefAttributes<TableRef>) => ReactElement | null) & {
  Skeleton: (props: SkeletonProps) => ReactElement
}

interface SkeletonProps extends ViewProps {
  cols: TableCol<any>[]
  hasAccessory?: boolean
  numberOfRows?: number
  condensed?: boolean
  contentStyle?: StyleProp<ViewStyle>
}

Table.Skeleton = (props: SkeletonProps) => {
  const { cols, hasAccessory, numberOfRows = 10, condensed, contentStyle, style, ...otherProps } = props

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <Head cols={cols} style={[styles.row, styles.head]} hasAccessory={hasAccessory} />
      <FlatList
        data={Array.from({ length: numberOfRows })}
        renderItem={() => (
          <Row.Skeleton
            cols={cols}
            hasAccessory={hasAccessory}
            condensed={condensed}
            style={styles.row}
            contentStyle={contentStyle}
          />
        )}
      />
    </View>
  )
}

export default Table
